
import {
  Box,
  Button,
  Card,
  CardContent,
  Select,
  Modal,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Pagination,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState, useCallback } from "react";
import axiosClient from "../axiosClient";
import { useTranslation } from "react-i18next";

const Notes = () => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [noteStatus, setNoteStatus] = useState("");
  const [notes, setNotes] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState(null);
  const [message, setMessage] = useState("");
  const [modalError, setModalError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [availableNotesCount, setAvailableNotesCount] = useState({ download: 0, delete: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { t } = useTranslation();

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setNote("");
    setNoteStatus("");
    setModalError("");
  };

  const handleClose = () => {
    setOpen(false);
    setNote("");
    setNoteStatus("");
    setEditNoteId(null);
    setEditMode(false);
    setModalError("");
  };

  const handleEdit = (note) => {
    setEditMode(true);
    setEditNoteId(note.id);
    setNote(note.content);
    setNoteStatus(note.status);
    setOpen(true);
    setModalError("");
  };

  const handleSave = async () => {
    if (!note || typeof note !== "string" || !note.trim()) {
      setModalError("Please fill out the required note field.");
      return;
    }
    if (!noteStatus || !["open", "pending", "closed"].includes(noteStatus)) {
      setModalError("Please select a valid status (Open, Pending, or Closed).");
      return;
    }

    try {
      if (editMode) {
        const response = await axiosClient.put(`/api/create-note/`, {
          note_id: editNoteId,
          content: note,
          status: noteStatus,
        });
        setNotes(notes.map((n) => (n.id === editNoteId ? response.data : n)));
      } else {
        const response = await axiosClient.post("/api/create-note/", {
          content: note,
          status: noteStatus,
        });
        setNotes([...notes, response.data]);
      }
      setModalError("");
      handleClose();
      await fetchNotes(page);
    } catch (error) {
      setModalError(`Error saving note: ${error.response?.data || error.message}`);
    }
  };

  // Separate function for counting available notes
  const fetchAvailableNotesCount = useCallback(async () => {
    try {
      let downloadCount = 0;
      let deleteCount = 0;

      // For download count - only if status and date filters are set
      if (filterStatus && filterDate) {
        const downloadParams = {
          status: filterStatus,
          month: filterDate.getMonth() + 1,
          year: filterDate.getFullYear(),
        };

        try {
          const downloadRes = await axiosClient.get("/api/create-note/", {
            params: downloadParams,
          });
          downloadCount = downloadRes.data.count || 0;
        } catch (error) {
          downloadCount = 0;
        }
      }

      // For delete count - only if date filter is set
      if (filterDate) {
        const deleteParams = {
          status: "closed",
          month: filterDate.getMonth() + 1,
          year: filterDate.getFullYear(),
        };

        try {
          const deleteRes = await axiosClient.get("/api/create-note/", {
            params: deleteParams,
          });
          deleteCount = deleteRes.data.count || 0;
        } catch (error) {
          deleteCount = 0;
        }
      }

      setAvailableNotesCount({
        download: downloadCount,
        delete: deleteCount,
      });
    } catch (error) {
      setAvailableNotesCount({ download: 0, delete: 0 });
    }
  }, [filterStatus, filterDate]);

  // Simplified fetchNotes function
  const fetchNotes = useCallback(async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const params = { page: pageNum };
      if (filterStatus) params.status = filterStatus;
      if (filterDate) {
        params.month = filterDate.getMonth() + 1;
        params.year = filterDate.getFullYear();
      }

      const res = await axiosClient.get("/api/create-note/", { params });
      const { count, results } = res.data;
      setNotes(results);
      setTotalCount(count);
      const pageSize = 3;
      setTotalPages(Math.ceil(count / pageSize));
      setPage(pageNum);
      setMessage(results.length === 0 ? "No notes found." : "");
    } catch (error) {
      setMessage("Error fetching notes.");
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterDate]);

  // Update useEffect to call both functions when filters change
  useEffect(() => {
    fetchNotes(page);
    fetchAvailableNotesCount();
  }, [fetchNotes, fetchAvailableNotesCount, filterStatus, filterDate, page]);

  // Separate useEffect for pagination (only calls fetchNotes)
  const handlePageChange = (event, value) => {
    setPage(value);
    fetchNotes(value); // Only fetch notes, don't update counts
  };

  const handleDelete = async (noteId) => {
    try {
      await axiosClient.delete(`/api/create-note/`, {
        params: { note_id: noteId },
      });
      setNotes(notes.filter((note) => note.id !== noteId));
      setMessage("Note marked as deleted.");
      await fetchNotes(page);
    } catch (error) {
      setMessage("Error deleting the note.");
    }
  };

  const handleDownload = async () => {
    if (!filterStatus || !filterDate) {
      setMessage("Please select a valid status and date to download.");
      return;
    }

    const monthLabel = filterDate.toLocaleString("default", { month: "long" });
    const year = filterDate.getFullYear();
    try {
      setMessage(`Downloading ${filterStatus} notes tabular data for ${monthLabel} ${year}...`);
      const response = await axiosClient.get("/api/download-notes/", {
        params: {
          status: filterStatus,
          month: filterDate.getMonth() + 1,
          year: filterDate.getFullYear(),
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `notes_${filterStatus}_${year}_${filterDate.getMonth() + 1}.txt`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage(`Successfully downloaded ${filterStatus} notes for ${monthLabel} ${year}.`);
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
        "Error downloading notes. No notes may exist for the selected criteria."
      );
    }
  };

  const handleDeleteByMonth = () => {
    if (!filterDate) {
      setMessage("Please select a valid date to delete closed notes.");
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDeleteByMonth = async () => {
    const monthLabel = filterDate.toLocaleString("default", { month: "long" });
    const year = filterDate.getFullYear();
    try {
      setMessage(`Deleting closed notes for ${monthLabel} ${year}...`);
      const response = await axiosClient.post("/api/delete-notes/", {
        month: filterDate.getMonth() + 1,
        year: filterDate.getFullYear(),
      });
      setMessage(response.data.message);
      await fetchNotes(page);
    } catch (error) {
      setMessage(error.response?.data?.message || "Error deleting notes.");
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleClearFilters = () => {
    setFilterStatus("");
    setFilterDate(null);
    setPage(1);
  };

  // const monthLabel = filterDate ? filterDate.toLocaleString("default", { month: "long" }) : "Month";
  // const yearLabel = filterDate ? filterDate.getFullYear() : "Year";
  // const statusLabel = filterStatus
  //   ? filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)
  //   : "Status";

  // const downloadButtonText =
  //   availableNotesCount.download > 0 && filterStatus && filterDate
  //     ? `Download ${statusLabel} Notes for ${monthLabel}, ${yearLabel}`
  //     : "Download Notes";

  // const deleteButtonText =
  //   availableNotesCount.delete > 0 && filterStatus === "closed" && filterDate
  //     ? `Delete Closed Notes for ${monthLabel}, ${yearLabel}`
  //     : "Delete Closed Notes";

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, sm: 3 },
        }}
      >
        <Card
          elevation={16}
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: "600px", md: "800px" },
            mx: "auto",
          }}
        >
          <CardContent>
            <Button
              onClick={handleOpen}
              variant="contained"
              fullWidth
              sx={{ mb: 2 }}
            >
              {/* Create Notes */}
               {t("Create Notes")}
            </Button>

            <Alert
              severity="info"
              sx={{
                mb: 2,
                alignItems: "flex-start",
                "& .MuiAlert-message": {
                  p: 0,
                  pt: 0.5,
                },
              }}
            >
              {/* <Typography component="div" variant="body2" sx={{ textAlign: "left" }}>
                <strong>Instructions:</strong>
                <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                  <li>
                    To <strong>download notes</strong>, select a status and date, then click "Download Notes."
                  </li>
                  <li>
                    To <strong>delete notes</strong>, select "Closed" in Status and a date, then click "Delete Closed Notes." Only closed notes for the selected month & year can be bulk deleted.
                  </li>
                  <li>
                    <strong>Warning:</strong> Only the main admin can restore deleted notes.
                  </li>
                </ul>
              </Typography> */}
                <Typography component="div" variant="body2" sx={{ textAlign: "left" }}>
                <strong>{t("Instructions")}:</strong>
                <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                <li>{t("To download notes instruction")}</li>
                <li>{t("To delete notes instruction")}</li>
                <li>{t("Warning only main admin instruction")}</li>
                </ul>
                </Typography>
            </Alert>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  {/* <InputLabel>Status</InputLabel> */}
                   <InputLabel>{t("Status")}</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    // label="Status"
                      label={t("Status")}
                  >
                    {/* <MenuItem value="">All</MenuItem>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem> */}
                    <MenuItem value="">{t("All")}</MenuItem>
                    <MenuItem value="open">{t("Open")}</MenuItem>
                    <MenuItem value="pending">{t("Pending")}</MenuItem>
                    <MenuItem value="closed">{t("Closed")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  // label="Select Month and Year"
                  label={t("Select Month and Year")}
                  views={["year", "month"]}
                  value={filterDate}
                  onChange={(newValue) => setFilterDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Button
                onClick={handleDownload}
                variant="contained"
                color="secondary"
                disabled={
                  !filterStatus ||
                  !filterDate ||
                  availableNotesCount.download === 0
                }
                sx={{ whiteSpace: "normal", textAlign: "center" }}
              >
                {/* {downloadButtonText} */}
                 {t("Download Notes")}
              </Button>
              <Button
                onClick={handleDeleteByMonth}
                variant="contained"
                color="error"
                disabled={
                  filterStatus !== "closed" ||
                  !filterDate ||
                  availableNotesCount.delete === 0
                }
                sx={{ whiteSpace: "normal", textAlign: "center" }}
              >
                {/* {deleteButtonText} */}
                 {t("Delete Closed Notes")}
              </Button>
              <Button onClick={handleClearFilters} variant="outlined">
                {/* Clear Filters */}
                {t("Clear Filters")}
              </Button>
            </Box>

            {message && (
              <Typography
                color={message.includes("Error") ? "error" : "primary"}
                sx={{ mb: 2 }}
              >
                {message}
              </Typography>
            )}

            <Typography variant="body2" sx={{ mb: 2 }}>
              {/* Total Notes: {totalCount} */}
               {t("Total Notes")}: {totalCount}
            </Typography>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {notes.map((note) => (
                  <Card
                    key={note?.id}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {note.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(note.created_at).toLocaleString()}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={true}
                        sx={{
                          bgcolor:
                            note.status === "open"
                              ? "success.main"
                              : note.status === "pending"
                                ? "warning.main"
                                : "error.main",
                          color: "white",
                          "&.Mui-disabled": {
                            bgcolor:
                              note.status === "open"
                                ? "success.main"
                                : note.status === "pending"
                                  ? "warning.main"
                                  : "error.main",
                            color: "white",
                            opacity: 1,
                          },
                        }}
                      >
                        {/* {note.status.charAt(0).toUpperCase() + note.status.slice(1)} */}
                        {t(note.status.charAt(0).toUpperCase() + note.status.slice(1))}
                      </Button>
                      <Button
                        onClick={() => handleEdit(note)}
                        variant="outlined"
                        size="small"
                      >
                        {/* Edit */}
                         {t("Edit")}
                      </Button>
                      <Button
                        onClick={() => handleDelete(note.id)}
                        variant="outlined"
                        color="error"
                        size="small"
                      >
                        {/* Delete */}
                         {t("Delete")}
                      </Button>
                    </Box>
                  </Card>
                ))}
                {totalPages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: { xs: "90%", sm: "80%", md: "60%", lg: "40%" },
            maxWidth: "600px",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 3 },
            mx: "auto",
            mt: { xs: "10%", md: "5%" },
            overflowY: "auto",
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={4}
            required
            value={note}
            variant="outlined"
            onChange={(e) => setNote(e.target.value)}
            // placeholder="Enter your notes"
              placeholder={t("Enter your notes")}

            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="note-status">Status</InputLabel>
            <Select
              labelId="note-status"
              required
              value={noteStatus}
              onChange={(e) => setNoteStatus(e.target.value)}
              // label="Status"
              label={t("Status")}
            >
              {/* <MenuItem value={"open"}>Open</MenuItem>
              <MenuItem value={"pending"}>Pending</MenuItem>
              <MenuItem value={"closed"}>Closed</MenuItem> */}

              <MenuItem value={"open"}>{t("Open")}</MenuItem>
              <MenuItem value={"pending"}>{t("Pending")}</MenuItem>
              <MenuItem value={"closed"}>{t("Closed")}</MenuItem>
            </Select>
          </FormControl>
          {modalError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {modalError}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSave}
            fullWidth
            sx={{ mt: 2 }}
          >
            {/* {editMode ? "Update" : "Submit"} */}
            {editMode ? t("Update") : t("Submit")}
          </Button>
        </Box>
      </Modal>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        {/* <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle> */}
        <DialogTitle id="delete-dialog-title">
          {t("Confirm Deletion")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {/* Are you sure you want to delete all closed notes for{" "}
            {filterDate?.toLocaleString("default", { month: "long" })} {filterDate?.getFullYear()}? */}
              {t("Are you sure you want to delete all closed notes for this period?")}
            <br />This action cannot be undone by you. Only the main admin can restore these notes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {/* Cancel */}
            {t("Cancel")}
          </Button>
          <Button onClick={confirmDeleteByMonth} color="error" autoFocus>
            {/* Delete */}
               {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default Notes;